import { useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faBan, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { apiKeysAdminApi } from '../../../services/admin/apiKeysAdminApi'
import type {
  AdminApiKey,
  ApiKeyUsageResponse,
  ApiKeyUsagePoint,
} from '../../../types/apiKeyAdmin'

const WINDOWS = [1, 7, 30]

/**
 * Hourly request volume. Deliberately a bare sparkline rather than a charting
 * dependency: what an admin is looking for is a shape change — a flat line that
 * suddenly spikes, or a busy key that goes silent — and that reads fine at this
 * fidelity.
 */
function Sparkline({ points }: { points: ApiKeyUsagePoint[] }) {
  if (points.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">No traffic in this window.</div>
  }

  const peak = Math.max(...points.map((p) => p.requests), 1)

  return (
    <div className="flex h-24 items-end gap-px overflow-x-auto">
      {points.map((point) => {
        const hasErrors = point.count_4xx + point.count_5xx > 0
        return (
          <div
            key={point.hour}
            className={`min-w-[3px] flex-1 rounded-t ${hasErrors ? 'bg-amber-400' : 'bg-blue-400'}`}
            style={{ height: `${Math.max((point.requests / peak) * 100, 2)}%` }}
            title={`${new Date(point.hour).toLocaleString()}\n${point.requests} requests\n${
              point.distinct_sources
            } source(s)\n${point.count_4xx} 4xx, ${point.count_5xx} 5xx`}
          />
        )
      })}
    </div>
  )
}

function Stat({
  label,
  value,
  alert,
  hint,
}: {
  label: string
  value: string | number
  alert?: boolean
  hint?: string
}) {
  return (
    <div className={`rounded border p-3 ${alert ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${alert ? 'text-amber-800' : 'text-gray-900'}`}>
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  )
}

/**
 * Per-key activity, shown before a revoke decision.
 *
 * The two stats worth reacting to are new sources in the last 24h (a key that
 * has started calling from somewhere new) and a high error rate (a caller
 * probing endpoints it isn't entitled to). Neither is proof on its own, which
 * is why this is a review screen and not an automatic block.
 */
export default function ApiKeyUsagePanel({
  apiKey,
  onClose,
  onRevoked,
}: {
  apiKey: AdminApiKey
  onClose: () => void
  onRevoked: () => void
}) {
  const [days, setDays] = useState(7)
  const [usage, setUsage] = useState<ApiKeyUsageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setUsage(await apiKeysAdminApi.usage(apiKey.id, days))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage')
    } finally {
      setLoading(false)
    }
  }, [apiKey.id, days])

  useEffect(() => {
    load()
  }, [load])

  const handleRevoke = async () => {
    const owner = apiKey.owner_email ?? apiKey.supabase_user_id
    if (!window.confirm(`Revoke ${apiKey.preview} belonging to ${owner}?`)) return

    setRevoking(true)
    try {
      await apiKeysAdminApi.revoke(apiKey.id)
      onRevoked()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key')
      setRevoking(false)
    }
  }

  const summary = usage?.summary

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {apiKey.label ?? 'API key'}{' '}
              <span className="font-mono text-sm text-gray-500">{apiKey.preview}</span>
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {apiKey.owner_email ?? apiKey.supabase_user_id}
              {apiKey.never_expires && ' · never expires'}
            </p>
            {apiKey.justification && (
              <p className="mt-1 text-xs text-amber-700">
                Perpetual, authorized because: {apiKey.justification}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setDays(w)}
                className={`rounded px-3 py-1 text-sm ${
                  days === w
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {w === 1 ? '24 hours' : `${w} days`}
              </button>
            ))}
          </div>

          {loading && <div className="py-10 text-center text-sm text-gray-500">Loading usage…</div>}

          {!loading && summary && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat label="Requests" value={summary.total_requests.toLocaleString()} />
                <Stat
                  label="Distinct sources"
                  value={summary.distinct_sources}
                  hint="Unique client IPs"
                />
                <Stat
                  label="New sources 24h"
                  value={summary.new_sources_last_24h}
                  alert={summary.new_sources_last_24h > 0}
                  hint="Not seen before"
                />
                <Stat
                  label="Error rate"
                  value={`${(summary.error_rate * 100).toFixed(1)}%`}
                  alert={summary.error_rate > 0.25}
                />
              </div>

              {(summary.new_sources_last_24h > 0 || summary.error_rate > 0.25) && (
                <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4" />
                  <span>
                    Worth a look — but neither signal is proof on its own. A partner
                    redeploying to new infrastructure produces the same pattern as a
                    stolen key. Confirm with the partner before revoking.
                  </span>
                </div>
              )}

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">
                  Hourly volume{' '}
                  <span className="font-normal text-gray-500">
                    (peak {summary.peak_hour_requests}, average{' '}
                    {summary.hourly_average}/hour)
                  </span>
                </h3>
                <Sparkline points={usage.timeline} />
              </div>

              {usage.top_endpoints.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-900">Endpoints touched</h3>
                  <ul className="divide-y divide-gray-200 rounded border border-gray-200">
                    {usage.top_endpoints.map((entry) => (
                      <li
                        key={entry.endpoint}
                        className="flex justify-between px-3 py-2 text-sm"
                      >
                        <span className="font-mono text-gray-700">{entry.endpoint}</span>
                        <span className="text-gray-500">
                          {entry.hours_seen} hour{entry.hours_seen === 1 ? '' : 's'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {apiKey.status !== 'revoked' && (
          <div className="flex justify-end border-t border-gray-200 p-5">
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="inline-flex items-center rounded bg-red-600 px-5 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-gray-300"
            >
              <FontAwesomeIcon icon={faBan} className="mr-2 h-4 w-4" />
              {revoking ? 'Revoking…' : 'Revoke this key'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
